import React from 'react';
import { reasonLabel } from './dataConnectors';

const VERDICT_TEXT = { good: 'Delivers what you asked for', partial: 'Partly delivers what you asked for', poor: 'Does not deliver what you asked for' };

function coverageState(f) {
  if (!f.of) return 'none';
  if (f.filled === f.of) return 'ok';
  return f.filled ? 'warn' : 'bad';
}

function identityWarning(identity) {
  if (!identity || identity.verdict === 'match') return null;
  const what = identity.on === 'domain' ? `the website ${identity.expected}` : `"${identity.expected}"`;
  return identity.verdict === 'mismatch'
    ? `The result does not mention ${what} — it may be about a different company.`
    : `The result only partly matches ${what} — check that it is the right company.`;
}

/** Warnings first, then the field check and the AI review. */
export function QualityReport({ quality, keptPaths, onKeepPath, belongsWhy }) {
  if (!quality) return null;
  const { coverage = [], identity, review } = quality;
  const emptyFields = coverage.filter((f) => f.of && !f.filled);
  const warnings = [
    ...emptyFields.map((f) => `"${f.label}" is empty in every result.`),
    identityWarning(identity),
    belongsWhy ? `Probably the wrong match: ${belongsWhy}` : null,
    ...(review?.warnings || []),
  ].filter(Boolean);

  return (
    <div className="dcx-quality" aria-label="Quality check">
      <div className="dcx-quality-head">
        <i className="fa fa-clipboard-check" /> Quality check
        {review && <span className={`dcx-verdict dcx-verdict--${review.verdict}`}>{VERDICT_TEXT[review.verdict]}</span>}
      </div>
      {review?.summary && <p className="dcx-quality-summary">{review.summary}</p>}

      {warnings.length > 0 && (
        <ul className="dcx-warnings">
          {warnings.map((w) => <li key={w}><i className="fa fa-triangle-exclamation" /> <b>Attention:</b> {w}</li>)}
        </ul>
      )}

      {coverage.length > 0 && (
        <div className="dcx-coverage">
          {coverage.map((f) => (
            <span key={f.path} className={`dcx-cov dcx-cov--${coverageState(f)}`} title={`${f.filled} of ${f.of} results have a value`}>
              {f.label} <b>{f.filled}/{f.of}</b>
            </span>
          ))}
        </div>
      )}

      {review?.missing?.length > 0 && (
        <div className="dcx-missing">
          <span className="dcx-label">You asked for, but are not keeping yet</span>
          {review.missing.map((m) => (
            <div key={m.want} className="dcx-missing-row">
              <span><b>{m.want}</b>{m.note ? ` — ${m.note}` : ''}</span>
              {m.path && !keptPaths.has(m.path) && (
                <button type="button" className="dcx-link" onClick={() => onKeepPath(m.path, m.want)}>
                  <i className="fa fa-plus" /> Keep <code>{m.path}</code>
                </button>
              )}
              {m.path && keptPaths.has(m.path) && <span className="dcx-muted"><i className="fa fa-check" /> kept</span>}
            </div>
          ))}
        </div>
      )}

      {!review && !warnings.length && coverage.every((f) => coverageState(f) === 'ok') && (
        <p className="dcx-muted dcx-small"><i className="fa fa-circle-check" /> All kept fields are filled.</p>
      )}
    </div>
  );
}

function outcome(r) {
  if (r.reason === 'skipped') return { cls: 'skip', text: 'Not tested' };
  if (!r.ok) return { cls: r.reason === 'missing_input' ? 'warn' : 'bad', text: reasonLabel(r.reason) };
  if (!r.total) return { cls: 'warn', text: 'Nothing found' };
  return { cls: 'ok', text: `${r.total} result${r.total === 1 ? '' : 's'}` };
}

/** "Found data for 3 of 5 contacts" plus one row per contact. */
export function BatchReport({ batch, inputs, keptPaths, onKeepPath, inputLabelFor }) {
  if (!batch) return null;
  const { results, summary, coverage, review, stopped } = batch;
  const belongs = Object.fromEntries((review?.belongs || []).map((b) => [b.contactId, b]));
  const hints = [];
  if (stopped) hints.push(`Stopped early: ${reasonLabel(stopped)}. This affects every contact, so the rest was not called.`);
  if (summary.tested && summary.empty * 2 >= summary.tested && inputs.includes('business_name')) {
    hints.push('Many contacts found nothing. This source may need the exact registered company name (e.g. with "GmbH").');
  }
  if (summary.missingInput) {
    hints.push(`${summary.missingInput} contact(s) lack ${inputs.map(inputLabelFor).join(' / ')} — they will be skipped when this runs.`);
  }
  const wrong = results.filter((r) => r.identity?.verdict === 'mismatch' || belongs[r.contactId]?.verdict === 'no').length;
  if (wrong) hints.push(`${wrong} result(s) look like a different company — check the rows marked below.`);

  return (
    <div className="dcx-batch" aria-label="Test on several contacts">
      <div className="dcx-batch-head">
        <span className={`dcx-hitrate dcx-hitrate--${summary.found === summary.tested ? 'ok' : summary.found ? 'warn' : 'bad'}`}>
          Found data for {summary.found} of {summary.tested} contacts
        </span>
        <span className="dcx-muted">
          {summary.empty ? ` · ${summary.empty} nothing found` : ''}
          {summary.missingInput ? ` · ${summary.missingInput} missing data` : ''}
          {summary.failed ? ` · ${summary.failed} failed` : ''}
        </span>
      </div>
      {hints.length > 0 && (
        <ul className="dcx-warnings">{hints.map((h) => <li key={h}><i className="fa fa-triangle-exclamation" /> {h}</li>)}</ul>
      )}
      <table className="dcx-batch-table">
        <tbody>
          {results.map((r) => {
            const o = outcome(r);
            const verdict = belongs[r.contactId];
            const off = r.identity?.verdict === 'mismatch' || verdict?.verdict === 'no';
            return (
              <tr key={r.contactId} className={off ? 'off' : ''}>
                <td className="dcx-batch-name">{r.name}{r.city ? <span className="dcx-muted"> · {r.city}</span> : null}</td>
                <td><span className={`dcx-out dcx-out--${o.cls}`}>{o.text}</span></td>
                <td className="dcx-batch-first">
                  {(r.first || []).slice(0, 2).map((v) => <span key={v.label}><b>{v.label}:</b> {v.value}</span>)}
                  {r.aiPicks?.length > 0 && <span className="dcx-muted">{r.aiPicks.map((p) => `${p.name}: ${p.value || '—'}`).join(' · ')}</span>}
                  {r.missing?.length > 0 && <span className="dcx-muted">no {r.missing.map(inputLabelFor).join(', ')}</span>}
                </td>
                <td className="dcx-batch-match">
                  {off && <span className="dcx-out dcx-out--bad" title={verdict?.why || ''}><i className="fa fa-user-xmark" /> other company?</span>}
                  {!off && (r.identity?.verdict === 'match' || verdict?.verdict === 'yes') && (
                    <span className="dcx-out dcx-out--ok"><i className="fa fa-user-check" /> matches</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <QualityReport quality={{ coverage, identity: null, review: review && { ...review, belongs: [] } }} keptPaths={keptPaths} onKeepPath={onKeepPath} />
    </div>
  );
}
